import { useState, useEffect, useRef } from "react";
import { apiErrorText } from '../lib/apiError';
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, Shield, Lock, Fingerprint, Smartphone, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config/api";
import { waGet } from "../lib/webauthn";

const PlatformAdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // step 1 = credentials, 2 = second factor
  const [step, setStep] = useState(1);
  const [methods, setMethods] = useState([]);            // ['fido','totp'] | ['totp'] | ['email']
  const [activeMethod, setActiveMethod] = useState("");  // 'fido' | 'totp' | 'recovery' | 'email'
  const [twoFACode, setTwoFACode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const fidoTried = useRef(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const completeLogin = (data) => {
    localStorage.setItem("jwtToken", data.token);
    localStorage.setItem("userType", "super_admin");
    localStorage.setItem("userData", JSON.stringify({
      username: data.username,
      name: data.name || "Super Admin",
      role_name: data.role_name,
      tab_permissions: data.tab_permissions || {},
      scope_own_orgs: data.scope_own_orgs || false,
      is_owner_role: data.is_owner_role || false,
      has_pin: data.has_pin || false,
    }));
    toast.success(`Welcome, ${data.name || data.username}!`);
    navigate("/super-admin");
  };

  // Step 1: verify credentials — server decides which 2FA methods apply
  const handleSendCredentials = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error("Please enter both username and password"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/v2/super-admin/auth/login`, { username, password });
      if (response.data.success && response.data.requires_2fa) {
        const m = response.data.methods || ["email"];
        setMethods(m);
        setActiveMethod(m[0]);
        setMaskedEmail(response.data.masked_destinations || response.data.masked_email || "your email");
        setTwoFACode("");
        setRecoveryCode("");
        fidoTried.current = false;
        if (m[0] === "email") setResendCooldown(45);
        setStep(2);
        if (m[0] === "email") toast.success("Verification code sent");
      } else if (response.data.token) {
        completeLogin(response.data);
      }
    } catch (error) {
      toast.error(apiErrorText(error, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  // ── FIDO security key ──────────────────────────────────────────────────────
  const handleFido = async () => {
    setLoading(true);
    try {
      const opts = await axios.post(`${API_URL}/v2/super-admin/auth/webauthn/login-options`, {
        username, origin: window.location.origin,
      });
      const credential = await waGet(opts.data);
      const res = await axios.post(`${API_URL}/v2/super-admin/auth/webauthn/login-verify`, {
        username, credential, origin: window.location.origin,
      });
      if (res.data.success) completeLogin(res.data);
    } catch (error) {
      if (error?.name === "NotAllowedError") {
        toast.error("Key touch cancelled or timed out");
      } else {
        toast.error(apiErrorText(error, "Security key verification failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-trigger the key prompt the moment the fido step appears
  useEffect(() => {
    if (step === 2 && activeMethod === "fido" && !fidoTried.current) {
      fidoTried.current = true;
      handleFido();
    }
  }, [step, activeMethod]); // eslint-disable-line

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/v2/super-admin/auth/send-otp`, { username });
      setMaskedEmail(response.data.masked_destinations || response.data.masked_email || maskedEmail);
      setResendCooldown(45);
      setTwoFACode("");
      document.getElementById('pin-0')?.focus();
      toast.success("New code sent");
    } catch (error) {
      toast.error(apiErrorText(error, "Failed to resend code"));
    } finally {
      setLoading(false);
    }
  };

  // Step 2 verify: email OTP or TOTP (same 6-digit UI, different endpoint)
  const handleVerify2FA = async (e) => {
    e?.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const endpoint = activeMethod === "totp" ? "verify-totp" : "verify-otp";
      const response = await axios.post(`${API_URL}/v2/super-admin/auth/${endpoint}`, {
        username, code: twoFACode,
      });
      if (response.data.success) completeLogin(response.data);
    } catch (error) {
      toast.error(apiErrorText(error, "Invalid code"));
      setTwoFACode("");
      setTimeout(() => document.getElementById('pin-0')?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleRecovery = async (e) => {
    e?.preventDefault();
    if (!recoveryCode.trim()) { toast.error("Enter a recovery code"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/v2/super-admin/auth/verify-totp`, {
        username, code: recoveryCode.trim(),
      });
      if (response.data.success) completeLogin(response.data);
    } catch (error) {
      toast.error(apiErrorText(error, "Invalid recovery code"));
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit the moment the 6th digit lands (typed or pasted)
  useEffect(() => {
    if (step === 2 && (activeMethod === "email" || activeMethod === "totp") && twoFACode.length === 6 && !loading) {
      handleVerify2FA();
    }
  }, [twoFACode, step]); // eslint-disable-line

  const switchMethod = (m) => { setActiveMethod(m); setTwoFACode(""); setRecoveryCode(""); if (m === "fido") { fidoTried.current = false; } };

  // ── Step 2 ─────────────────────────────────────────────────────────────────
  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="w-full max-w-sm text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto border border-blue-400/50">
              {activeMethod === "fido" ? <Fingerprint className="w-10 h-10 text-blue-400" /> :
               activeMethod === "totp" ? <Smartphone className="w-10 h-10 text-blue-400" /> :
               activeMethod === "recovery" ? <LifeBuoy className="w-10 h-10 text-blue-400" /> :
               <Lock className="w-10 h-10 text-blue-400" />}
            </div>
          </div>

          {/* FIDO — security key */}
          {activeMethod === "fido" && (
            <>
              <h1 className="text-2xl font-bold text-white mb-2">Security key required</h1>
              <p className="text-blue-200 text-sm mb-8">Insert your key and touch it when it blinks.</p>
              <Button onClick={handleFido} data-testid="platform-admin-fido-button" className="w-full h-12 text-base font-semibold bg-blue-500 hover:bg-blue-600 text-white" disabled={loading}>
                {loading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Waiting for key…</span> : <><Fingerprint className="w-4 h-4 mr-2"/>Use security key</>}
              </Button>
            </>
          )}

          {/* TOTP / email — 6-digit boxes */}
          {(activeMethod === "totp" || activeMethod === "email") && (
            <>
              <h1 className="text-2xl font-bold text-white mb-2">{activeMethod === "totp" ? "Authenticator code" : "Two-Factor Authentication"}</h1>
              <p className="text-blue-200 text-sm mb-8">
                {activeMethod === "totp"
                  ? "Enter the 6-digit code from your authenticator app"
                  : <>Code sent to<br/><span className="text-white font-medium">{maskedEmail}</span></>}
              </p>
              <form onSubmit={handleVerify2FA} className="space-y-6">
                <div className="flex justify-center gap-2" data-testid="platform-admin-otp-input">
                  {[0,1,2,3,4,5].map((i) => (
                    <input
                      key={i} id={`pin-${i}`}
                      type="text" inputMode="numeric" maxLength={1}
                      value={twoFACode[i] || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length <= 1) {
                          const arr = twoFACode.split('');
                          arr[i] = val;
                          setTwoFACode(arr.join(''));
                          if (val && i < 5) document.getElementById(`pin-${i+1}`)?.focus();
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === 'Backspace' && !twoFACode[i] && i > 0) document.getElementById(`pin-${i-1}`)?.focus(); }}
                      onPaste={(e) => { e.preventDefault(); setTwoFACode(e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6)); }}
                      disabled={loading}
                      autoFocus={i === 0}
                      className="w-12 h-14 text-center text-2xl font-bold bg-white/10 border border-blue-400/50 rounded-lg text-white focus:border-blue-400 focus:bg-white/20 focus:outline-none transition-all disabled:opacity-60"
                    />
                  ))}
                </div>
                <Button type="submit" data-testid="platform-admin-verify-button" className="w-full h-12 text-base font-semibold bg-blue-500 hover:bg-blue-600 text-white" disabled={loading || twoFACode.length !== 6}>
                  {loading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Verifying...</span> : "Verify & Sign In"}
                </Button>
              </form>
              {activeMethod === "email" && (
                <div className="mt-4 text-right">
                  <button onClick={handleResend} disabled={resendCooldown > 0 || loading} data-testid="platform-admin-resend-button" className="text-sm text-blue-300 hover:text-white font-medium transition-colors disabled:opacity-40">
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Recovery code */}
          {activeMethod === "recovery" && (
            <>
              <h1 className="text-2xl font-bold text-white mb-2">Recovery code</h1>
              <p className="text-blue-200 text-sm mb-8">Enter one of your saved one-time recovery codes.</p>
              <form onSubmit={handleRecovery} className="space-y-6">
                <Input
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder="XXXX-XXXX"
                  autoFocus
                  data-testid="platform-admin-recovery-input"
                  className="h-12 text-center text-xl font-mono tracking-widest bg-white/10 border border-blue-400/50 text-white placeholder:text-blue-300/40 focus:border-blue-400"
                />
                <Button type="submit" data-testid="platform-admin-recovery-button" className="w-full h-12 text-base font-semibold bg-blue-500 hover:bg-blue-600 text-white" disabled={loading || !recoveryCode.trim()}>
                  {loading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Verifying...</span> : "Verify & Sign In"}
                </Button>
              </form>
            </>
          )}

          {/* Method switcher */}
          <div className="mt-6 flex items-center justify-between text-sm">
            <button onClick={() => { setStep(1); setTwoFACode(''); }} data-testid="platform-admin-back-button" className="text-blue-300 hover:text-white transition-colors">← Back</button>
            <div className="flex gap-4">
              {methods.includes("fido") && activeMethod !== "fido" && (
                <button onClick={() => switchMethod("fido")} data-testid="switch-to-fido" className="text-blue-300 hover:text-white transition-colors">Use security key</button>
              )}
              {methods.includes("totp") && activeMethod !== "totp" && (
                <button onClick={() => switchMethod("totp")} data-testid="switch-to-totp" className="text-blue-300 hover:text-white transition-colors">Use authenticator</button>
              )}
              {methods.includes("totp") && activeMethod !== "recovery" && (
                <button onClick={() => switchMethod("recovery")} data-testid="switch-to-recovery" className="text-blue-300/70 hover:text-white transition-colors">Recovery code</button>
              )}
            </div>
          </div>
          {activeMethod === "email" && <p className="text-xs text-blue-300/70 mt-4">Code expires in 10 minutes</p>}
        </div>
      </div>
    );
  }

  // ── Step 1: Credentials ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: '#007AFF'}}>
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            BookingFlows
          </CardTitle>
          <p className="text-gray-600 mt-2">Platform Administrator Access</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendCredentials} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-semibold text-gray-700">Email or username</Label>
              <Input id="username" data-testid="platform-admin-username-input" type="text" placeholder="you@bookingflows.com" value={username} onChange={(e) => setUsername(e.target.value)} disabled={loading} className="h-12 text-base border border-gray-200 focus:border-blue-500" autoFocus />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-gray-700">Password</Label>
              <Input id="password" data-testid="platform-admin-password-input" type="password" placeholder="Enter admin password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} className="h-12 text-base border border-gray-200 focus:border-blue-500" />
            </div>
            <Button type="submit" data-testid="platform-admin-login-button" className="w-full h-12 text-base font-semibold mt-6" style={{background: '#007AFF'}} disabled={loading}>
              {loading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Verifying...</span> : <><Shield className="w-4 h-4 mr-2"/>Continue</>}
            </Button>
          </form>
        </CardContent>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
        </div>
      </Card>
    </div>
  );
};

export default PlatformAdminLogin;
